import React, { ReactNode } from 'react';

import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import TableHeadCellSortable from 'shared/components/Table/TableHead/TableHeadCellSortable';

import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import OpportunityNameCell from 'shared/components/Table/CellRenderers/OpportunityNameCell';
import CellDefault from 'shared/components/Table/CellRenderers/CellDefault';
import PossibleVolunteersCell from 'shared/components/Table/CellRenderers/PossibleVolunteersCell';
import OpportunityActionsCell from 'shared/components/Table/CellRenderers/OpportunityActionsCell';
import FullNameCell from 'shared/components/Table/CellRenderers/FullNameCell';
import ButtonPencil from 'shared/components/Buttons/ButtonPencil';

import {
	ADMIN_SEARCH_FILTER_TYPE,
	IOpportunityResponse,
	OPPORTUNITY_STATUSES,
	SEARCH_OPPORTUNITIES_POSSIBLE_SORT,
	SortDirection,
	SearchOpportunitiesSort,
} from '@joc/api-gateway';

import {
	convertOppoDateToHoursAndMinutes,
	formatDate,
	generateLocation,
	getImageType,
	getOpportunityImagePath,
} from 'core/functions';

import { formatCategory, formatDurationHoursMinutes, getChallengeOpportunityOrganizationLabel } from '../mappers';
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES } from 'core/constants';
import SuperAdminManualOpportunityActionsNew from './SuperAdminManualOpportunityActionsNew';
import SuperAdminPrivateDeleteButtonNew from './SuperAdminPrivateDeleteButtonNew';

// eslint-disable-next-line no-unused-vars
type HandleSortChange = (_sortBy: SEARCH_OPPORTUNITIES_POSSIBLE_SORT, _direction?: SortDirection) => void;

export type TableColumnConfigContext = {
	opportunity: IOpportunityResponse;
	opportunityFilterType: ADMIN_SEARCH_FILTER_TYPE;
	isPrintableTable: boolean;
	inPastOppoList: boolean;
	// eslint-disable-next-line no-unused-vars
	onEdit: (_opportunity: IOpportunityResponse) => void;
	// eslint-disable-next-line no-unused-vars
	onView: (_opportunity: IOpportunityResponse) => void;
	sortLocal?: SearchOpportunitiesSort;
	handleSortChange: HandleSortChange;
};

export type TableColumnConfig = {
	id: string;
	hideOnPrint?: boolean;
	// eslint-disable-next-line no-unused-vars
	renderHeader: (_args: { sortLocal?: SearchOpportunitiesSort; handleSortChange: HandleSortChange }) => ReactNode;
	// eslint-disable-next-line no-unused-vars
	renderCell: (_ctx: TableColumnConfigContext) => ReactNode;
};

export type TableConfig = {
	callChild: string;
	columns: Array<TableColumnConfig>;
};

const renderOptionsCell = (ctx: TableColumnConfigContext) => {
	if (ctx.isPrintableTable) return null;

	if (ctx.opportunity.status === OPPORTUNITY_STATUSES.PENDING) {
		return (
			<SuperAdminManualOpportunityActionsNew
				opportunityId={ctx.opportunity.id}
				editClickHandler={() => ctx.onEdit(ctx.opportunity)}
				opportunityFilterType={ctx.opportunityFilterType}
			/>
		);
	}

	return <OpportunityActionsCell opportunityData={ctx.opportunity} />;
};

export const getTableConfig = (opportunityFilterType: ADMIN_SEARCH_FILTER_TYPE): TableConfig => {
	const isChallenge = opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.CHALLENGE;
	const isSolo = opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.SOLO;
	const isPrivate = opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.PRIVATE;

	if (isChallenge) {
		return {
			callChild: 'challengeOpportunitiesList',
			columns: [
				{
					id: 'logo',
					renderHeader: () => (
						<TableHeadCell
							itemClassName={TABLE_ITEMS_CLASSNAMES.logo}
							text={TABLE_HEADERS.logo}
							isSortDisabled={true}
						/>
					),
					renderCell: ctx => (
						<AvatarCell
							imagePath={getOpportunityImagePath(ctx.opportunity)}
							opportunityType={getImageType(ctx.opportunity)}
							opportunity={ctx.opportunity}
							withPopup={true}
						/>
					),
				},
				{
					id: 'challenge',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.challenge}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Challenge}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<OpportunityNameCell
							opportunityName={ctx.opportunity.opportunityName}
							opportunityType={ctx.opportunity.opportunityType}
							volunteers={ctx.opportunity.volunteers}
							adminId={ctx.opportunity?.user?.id}
							challengeId={ctx.opportunity?.challengeId}
							isInitialOppo={ctx.opportunity.isInitial}
							isVerified={ctx.opportunity.isVerificated}
							onClick={() => ctx.onView(ctx.opportunity)}
						/>
					),
				},
				{
					id: 'date',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.date}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.StartDay}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.date}
							text={formatDate(ctx.opportunity.endDate)}
						/>
					),
				},
				{
					id: 'duration',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.duration}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Duration}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.duration}
							text={formatDurationHoursMinutes(ctx.opportunity.duration)}
						/>
					),
				},
				{
					id: 'organization',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.organization}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Organisation}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.organization}
							text={getChallengeOpportunityOrganizationLabel(ctx.opportunity)}
						/>
					),
				},
				{
					id: 'volunteer',
					renderHeader: () => (
						<TableHeadCell
							itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
							text={TABLE_HEADERS.volunteer}
							isSortDisabled={true}
						/>
					),
					renderCell: ctx => (
						<FullNameCell
							firstName={ctx.opportunity.user?.firstName}
							lastName={ctx.opportunity.user?.lastName}
							disableTextStyle={true}
						/>
					),
				},
				{
					id: 'options',
					hideOnPrint: true,
					renderHeader: () => <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />,
					renderCell: ctx => renderOptionsCell(ctx),
				},
			],
		};
	}

	if (isSolo) {
		return {
			callChild: 'oppotunities',
			columns: [
				{
					id: 'logo',
					renderHeader: () => (
						<TableHeadCell
							itemClassName={TABLE_ITEMS_CLASSNAMES.logo}
							text={TABLE_HEADERS.logo}
							isSortDisabled={true}
						/>
					),
					renderCell: ctx => (
						<AvatarCell
							imagePath={getOpportunityImagePath(ctx.opportunity as any)}
							opportunityType={getImageType(ctx.opportunity as any)}
							opportunity={ctx.opportunity as any}
							withPopup={true}
						/>
					),
				},
				{
					id: 'chesedName',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.chesedName}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.OpportunityName}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<OpportunityNameCell
							opportunityName={ctx.opportunity.opportunityName}
							opportunityType={ctx.opportunity.opportunityType}
							volunteers={ctx.opportunity.volunteers as any}
							adminId={ctx.opportunity?.user?.id}
							challengeId={ctx.opportunity?.challengeId}
							isInitialOppo={ctx.opportunity.isInitial}
							isVerified={ctx.opportunity.isVerificated}
							onClick={() => ctx.onView(ctx.opportunity)}
						/>
					),
				},
				{
					id: 'date',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.date}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.StartDay}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.date}
							text={formatDate(ctx.opportunity.endDate)}
						/>
					),
				},
				{
					id: 'duration',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.duration}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Duration}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.time}
							text={formatDurationHoursMinutes(ctx.opportunity.duration)}
						/>
					),
				},
				{
					id: 'category',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title="Category"
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Tag}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.type}
							text={formatCategory((ctx.opportunity as any).tag)}
						/>
					),
				},
				{
					id: 'organization',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.organization}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Organisation}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.organization}
							text={ctx.opportunity.organisation?.organizationName || ''}
						/>
					),
				},
				{
					id: 'volunteer',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.volunteer}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Coordinator}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<FullNameCell
							firstName={ctx.opportunity.user?.firstName}
							lastName={ctx.opportunity.user?.lastName}
							disableTextStyle={true}
						/>
					),
				},
				{
					id: 'email',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.email}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.CoordinatorEmail}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.email}
							text={ctx.opportunity.user?.email || ''}
						/>
					),
				},
				{
					id: 'options',
					hideOnPrint: true,
					renderHeader: () => <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />,
					renderCell: ctx => renderOptionsCell(ctx),
				},
			],
		};
	}

	if (isPrivate) {
		return {
			callChild: 'opportunitiesPrivate',
			columns: [
				{
					id: 'logo',
					renderHeader: () => (
						<TableHeadCell
							itemClassName={TABLE_ITEMS_CLASSNAMES.logo}
							text={TABLE_HEADERS.logo}
							isSortDisabled={true}
						/>
					),
					renderCell: ctx => (
						<AvatarCell
							imagePath={getOpportunityImagePath(ctx.opportunity as any)}
							opportunityType={getImageType(ctx.opportunity as any)}
							opportunity={ctx.opportunity as any}
							withPopup={true}
						/>
					),
				},
				{
					id: 'chesedName',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.chesedName}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.OpportunityName}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<OpportunityNameCell
							opportunityName={ctx.opportunity.opportunityName}
							opportunityType={ctx.opportunity.opportunityType}
							volunteers={ctx.opportunity.volunteers as any}
							adminId={ctx.opportunity?.user?.id}
							challengeId={ctx.opportunity?.challengeId}
							isInitialOppo={ctx.opportunity.isInitial}
							isVerified={ctx.opportunity.isVerificated}
							onClick={() => ctx.onView(ctx.opportunity)}
						/>
					),
				},
				{
					id: 'date',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.date}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.StartDay}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.date}
							text={formatDate(ctx.opportunity.endDate)}
						/>
					),
				},
				{
					id: 'duration',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.duration}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Duration}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.time}
							text={formatDurationHoursMinutes(ctx.opportunity.duration)}
						/>
					),
				},
				{
					id: 'volunteer',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.volunteer}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Coordinator}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<FullNameCell
							firstName={ctx.opportunity.user?.firstName}
							lastName={ctx.opportunity.user?.lastName}
							disableTextStyle={true}
						/>
					),
				},
				{
					id: 'email',
					renderHeader: ({ sortLocal, handleSortChange }) => (
						<TableHeadCellSortable
							title={TABLE_HEADERS.email}
							sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.CoordinatorEmail}
							handleSortChange={handleSortChange}
							sort={sortLocal}
						/>
					),
					renderCell: ctx => (
						<CellDefault
							parentClassName={TABLE_ITEMS_CLASSNAMES.email}
							text={ctx.opportunity.user?.email || ''}
						/>
					),
				},
				{
					id: 'edit',
					renderHeader: () => <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />,
					renderCell: ctx => (
						<div style={{ display: 'flex', justifySelf: 'self-end' }}>
							<ButtonPencil clickHandler={() => ctx.onEdit(ctx.opportunity)} />
						</div>
					),
				},
				{
					id: 'delete',
					renderHeader: () => <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />,
					renderCell: ctx => (
						<SuperAdminPrivateDeleteButtonNew
							opportunityId={ctx.opportunity.id}
							opportunityFilterType={ctx.opportunityFilterType}
						/>
					),
				},
			],
		};
	}

	// Legacy non-challenge layout (ALL/UPCOMING/PAST in UI).
	return {
		callChild: 'oppotunities',
		columns: [
			{
				id: 'logo',
				renderHeader: () => (
					<TableHeadCell
						itemClassName={TABLE_ITEMS_CLASSNAMES.logo}
						text={TABLE_HEADERS.logo}
						isSortDisabled={true}
					/>
				),
				renderCell: ctx => (
					<AvatarCell
						imagePath={getOpportunityImagePath(ctx.opportunity as any)}
						opportunityType={getImageType(ctx.opportunity as any)}
						opportunity={ctx.opportunity as any}
						withPopup={true}
					/>
				),
			},
			{
				id: 'chesedName',
				renderHeader: ({ sortLocal, handleSortChange }) => (
					<TableHeadCellSortable
						title={TABLE_HEADERS.chesedName}
						sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.OpportunityName}
						handleSortChange={handleSortChange}
						sort={sortLocal}
					/>
				),
				renderCell: ctx => (
					<OpportunityNameCell
						opportunityName={ctx.opportunity.opportunityName}
						opportunityType={ctx.opportunity.opportunityType}
						volunteers={ctx.opportunity.volunteers}
						adminId={ctx.opportunity?.user?.id}
						challengeId={ctx.opportunity?.challengeId}
						isInitialOppo={ctx.opportunity.isInitial}
						isVerified={ctx.opportunity.isVerificated}
						onClick={() => ctx.onView(ctx.opportunity)}
					/>
				),
			},
			{
				id: 'date',
				renderHeader: ({ sortLocal, handleSortChange }) => (
					<TableHeadCellSortable
						title={TABLE_HEADERS.date}
						sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.StartDay}
						handleSortChange={handleSortChange}
						sort={sortLocal}
					/>
				),
				renderCell: ctx => (
					<CellDefault
						parentClassName={TABLE_ITEMS_CLASSNAMES.date}
						text={formatDate(ctx.opportunity.endDate)}
					/>
				),
			},
			{
				id: 'start',
				renderHeader: ({ sortLocal, handleSortChange }) => (
					<TableHeadCellSortable
						title={TABLE_HEADERS.start}
						sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.StartTime}
						handleSortChange={handleSortChange}
						sort={sortLocal}
					/>
				),
				renderCell: ctx => (
					<CellDefault
						parentClassName={TABLE_ITEMS_CLASSNAMES.time}
						text={
							convertOppoDateToHoursAndMinutes(ctx.opportunity.startDate, ctx.opportunity.endDate)
								.startDate
						}
					/>
				),
			},
			{
				id: 'end',
				renderHeader: ({ sortLocal, handleSortChange }) => (
					<TableHeadCellSortable
						title={TABLE_HEADERS.end}
						sortBy={SEARCH_OPPORTUNITIES_POSSIBLE_SORT.EndTime}
						handleSortChange={handleSortChange}
						sort={sortLocal}
					/>
				),
				renderCell: ctx => (
					<CellDefault
						parentClassName={TABLE_ITEMS_CLASSNAMES.time}
						text={
							convertOppoDateToHoursAndMinutes(ctx.opportunity.startDate, ctx.opportunity.endDate).endDate
						}
					/>
				),
			},
			{
				id: 'location',
				renderHeader: () => (
					<TableHeadCell
						itemClassName={TABLE_ITEMS_CLASSNAMES.location}
						text={TABLE_HEADERS.location}
						isSortDisabled={true}
					/>
				),
				renderCell: ctx => (
					<CellDefault
						parentClassName={TABLE_ITEMS_CLASSNAMES.location}
						text={
							ctx.opportunity.isVirtual
								? ctx.opportunity.opportunityUrl
								: generateLocation(ctx.opportunity.address)
						}
					/>
				),
			},
			{
				id: 'coordinator',
				renderHeader: () => (
					<TableHeadCell
						itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
						text={TABLE_HEADERS.coordinator}
						isSortDisabled={true}
					/>
				),
				renderCell: ctx => (
					<FullNameCell
						firstName={ctx.opportunity.user?.firstName}
						lastName={ctx.opportunity.user?.lastName}
						disableTextStyle={true}
					/>
				),
			},
			{
				id: 'volunteers',
				renderHeader: () => (
					<TableHeadCell
						itemClassName={TABLE_ITEMS_CLASSNAMES.volunteers}
						text={TABLE_HEADERS.volunteers}
						isSortDisabled={true}
					/>
				),
				renderCell: ctx => (
					<PossibleVolunteersCell
						volunteers={ctx.opportunity.volunteers}
						opportunityId={ctx.opportunity.id}
						isApprovalRequired={ctx.opportunity.isApprovalRequired}
						opportunityName={ctx.opportunity.opportunityName}
						opportunityStatus={ctx.opportunity.status}
						opportunityType={ctx.opportunity.opportunityType}
						inPastOppoList={ctx.inPastOppoList}
					/>
				),
			},
			{
				id: 'options',
				hideOnPrint: true,
				renderHeader: () => <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />,
				renderCell: ctx => renderOptionsCell(ctx),
			},
		],
	};
};
